/** Connected Facebook Pages / Instagram accounts — connect via Meta login, enable/disable, disconnect. */
import React, { useState } from 'react';
import { Facebook, Instagram, Link2, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { wdel, wget, wput } from '@/components/website/api';

const BRANDS = [['all', 'All brands'], ['probound', 'ProBound'], ['proepoxy', 'ProEpoxy'], ['prodecor', 'ProDecor']];

export function ConnectedAccounts({ status, onChanged, staff }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { const r = await wget('/social/meta/connect'); window.location.href = r.url; }
    catch (e) { toast.error(e.message); setBusy(false); }
  };
  const patch = async (a, body) => { try { await wput(`/social/accounts/${a.page_id}`, body); onChanged(); } catch (e) { toast.error(e.message); } };
  const remove = async (a) => {
    if (!window.confirm(`Disconnect ${a.page_name}? Scheduled posts to it will fail.`)) return;
    try { await wdel(`/social/accounts/${a.page_id}`); toast.success('Page disconnected'); onChanged(); } catch (e) { toast.error(e.message); }
  };
  const accounts = status?.accounts || [];
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="social-accounts">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-slate-900 flex items-center gap-2"><Link2 className="w-5 h-5 text-blue-700" /> {staff ? 'Ox Resin pages' : 'Your Facebook & Instagram'}</h2>
          <p className="text-xs text-slate-500 mt-1">Log in with the Facebook account that manages {staff ? 'the Ox Resin / ProBound / ProEpoxy / ProDecor pages' : 'your business page'}. Instagram posts need a Business or Creator account linked to that page.</p>
        </div>
        <Button onClick={connect} disabled={busy || !status?.configured} className="bg-[#1877F2] hover:bg-[#145dbf] text-white h-10" data-testid="social-connect-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Facebook className="w-4 h-4 mr-2" />}{accounts.length ? 'Reconnect / add pages' : 'Connect Facebook & Instagram'}
        </Button>
      </div>
      {!status?.configured && <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="social-not-configured">Facebook posting isn't switched on yet{staff ? ' — add the Meta App ID and App Secret (see the set-up guide below).' : ' — Ox Resin are setting it up.'}</p>}
      {accounts.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100" data-testid="social-accounts-list">
          {accounts.map((a) => (
            <li key={a.page_id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`social-account-${a.page_id}`}>
              {a.picture ? <img src={a.picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center"><Facebook className="w-5 h-5" /></div>}
              <div className="flex-1 min-w-[160px]">
                <div className="font-semibold text-slate-900 text-sm">{a.page_name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Facebook className="w-3 h-3" /> Facebook Page</span>
                  {a.ig_user_id ? <span className="inline-flex items-center gap-1 text-pink-700"><Instagram className="w-3 h-3" /> @{a.ig_username || 'instagram'}</span> : <span className="italic">no Instagram linked</span>}
                  {a.needs_reauth && <span className="inline-flex items-center gap-1 text-rose-700 font-semibold" data-testid={`social-account-reauth-${a.page_id}`}><AlertTriangle className="w-3 h-3" /> login expired — reconnect</span>}
                </div>
              </div>
              {staff && (
                <select value={a.brand || 'all'} onChange={(e) => patch(a, { brand: e.target.value })} className="h-9 text-xs border border-slate-300 rounded-md px-2 bg-white" data-testid={`social-account-brand-${a.page_id}`}>
                  {BRANDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
              <label className="inline-flex items-center gap-2 text-xs text-slate-600"><Switch checked={a.enabled !== false} onCheckedChange={(v) => patch(a, { enabled: v })} data-testid={`social-account-enabled-${a.page_id}`} /> {a.enabled !== false ? 'On' : 'Off'}</label>
              <button type="button" onClick={() => remove(a)} className="p-1.5 text-slate-400 hover:text-rose-600" aria-label="Disconnect" data-testid={`social-account-remove-${a.page_id}`}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
